import { inject, Injectable } from '@angular/core';
import { EmojiData, EmojiService } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { EventType, MatrixEvent, RelationType, TimelineEvents } from 'matrix-js-sdk';
import { ReactionDto } from '../../../components/sb-client/sb-model/reaction-dto';
import { Room } from '../../../../shared-api/room';
import { ISendEventResponse } from 'matrix-js-sdk/lib/@types/requests';
import { MatrixService } from '../../matrix/matrix.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Dieser Service verwaltet Reaktionen innerhalb des Matrix-Chats.
 * Er ermöglicht das **Hinzufügen, Entfernen und Verwalten von Reaktionen** auf Nachrichten.
 * Zusätzlich stellt er sicher, dass die **Unicode-Längenbeschränkung** gemäß AFO A_26227-01 eingehalten wird.
 *
 * Funktionen:
 * - Verwaltung der Sichtbarkeit von Reaktionsleisten und Buttons.
 * - Hinzufügen und Entfernen von Emoji-Reaktionen zu Nachrichten.
 * - Sicherstellen, dass nur **einzelne Unicode-Zeichen** als Reaktionen verwendet werden.
 * - Interaktion mit dem Matrix-Protokoll zur Verwaltung von `m.reaction`-Events.
 *
 * Hierdurch erfüllte AFO`s
 * - A_25395-01 - Matrix Module (Event Annotations and Reactions)
 *   - Fähigkeit zur Verarbeitung und Anzeige.
 *   - Fähigkeit zur Erzeugung.
 * - A_26227-01 - Längenbegrenzung von Annotationen in Reactions
 */
export class ReactionsService {
  private readonly emojiService = inject(EmojiService);
  private readonly clientService = inject(MatrixService);

  public isSingleUnicodeCharacter(emoji: string | undefined): boolean {
    if (!emoji) {
      return false;
    }
    return [...emoji].length === 1;
  }

  getIdFromUnicode(unicode: string): string {
    const codepoints = Array.from(unicode)
      .map(char => {
        const codePoint = char.codePointAt(0);
        return codePoint === undefined ? '' : codePoint.toString(16).toUpperCase();
      })
      .filter(codePoint => codePoint !== '');
    const unified = codepoints.join('-');
    return this.emojiService.getData(unified)?.id ?? '';
  }

  /**
   * Angeklickte Emojis von anderen Usern werden als eigene Reaktion nochmal angehängt.
   * Wird ein selbst abgesendeter Emoji angeklickt, wird dieser entfernt.
   */
  async checkReaction(
    reactionsArray: ReactionDto[],
    emoji: string,
    events: MatrixEvent[],
    messageEventId: string | undefined,
    room: Room | undefined
  ): Promise<void> {
    const roomId = room?.roomId ?? '';
    const userId = this.clientService.client.getUserId();
    const ownReaction = events.find(event => event.getSender() === userId);

    if (ownReaction) {
      const id = ownReaction.getId();
      if (id) {
        await this.removeReaction(roomId, id);
      }
    } else if (messageEventId) {
      const pastEventId = this.getUserReactionEventId(reactionsArray);
      await this.deleteOldReactionAndSendNew(roomId, messageEventId, emoji, pastEventId);
    } else {
      throw new Error('Ungültige EventID für eine Reaktion.');
    }
  }

  private async removeReaction(roomId: string, eventId: string): Promise<ISendEventResponse> {
    return await this.clientService.client.redactEvent(roomId, eventId);
  }

  /**
   * Prüft, ob mindestens eine von den parametrisierten Reaktionen von dem User selbst abgesendet wurden.
   * Ist dies der Fall, wird die EventId der entsprechenden Reaktion zurückgegeben.
   */
  private getUserReactionEventId(reactionsArray: ReactionDto[]): string | undefined {
    const userId = this.clientService.client.getUserId();

    return (reactionsArray ?? [])
      .flatMap(reaction => reaction.events)
      .find(event => event.getSender() === userId)
      ?.getId();
  }

  async sendReaction(
    reactionsArray: ReactionDto[],
    eventId: string | undefined,
    selectedEmoji: EmojiData,
    room: Room | undefined
  ): Promise<void> {
    const pastEventId = this.getUserReactionEventId(reactionsArray);
    const roomId = room?.roomId;

    if (!roomId || !eventId || !selectedEmoji.native || !this.isSingleUnicodeCharacter(selectedEmoji.native)) {
      throw new Error('Ungültige Parameter für eine Reaktion.');
    }

    await this.deleteOldReactionAndSendNew(roomId, eventId, selectedEmoji.native, pastEventId);
  }

  /**
   * Existiert eine alte Reaktion, wird diese als erstes entfernt.
   * Im Anschluss wird die neue Reaktion gesendet.
   */
  private async deleteOldReactionAndSendNew(
    roomId: string,
    eventId: string,
    emoji: string,
    pastEventId: string | undefined
  ): Promise<void> {
    if (pastEventId) {
      await this.removeReaction(roomId, pastEventId);
    }
    await this.sendReactionToRoom(roomId, eventId, emoji);
  }

  private async sendReactionToRoom(roomId: string, eventId: string, emoji: string | undefined): Promise<void> {
    const content = {
      'm.relates_to': {
        event_id: eventId,
        key: emoji,
        rel_type: RelationType.Annotation
      }
    };

    await this.clientService.client.sendEvent(
      roomId,
      EventType.Reaction,
      content as TimelineEvents[EventType.Reaction]
    );
  }
}
